import React, {useEffect, useState} from "react";
import { Helmet } from "react-helmet";

import "../App.css";
import "../CSS/programmes/LabelPrinter.css";

import { ColorModeIcon } from "../context/ColorModeContext.tsx";
import Navbar from "../components/Navbar.tsx";
import CustomTextField from "../components/CustomTextField.tsx";
import { SubmitButton } from "../components/Buttons.tsx";
import CustomCheckbox from "../components/CustomCheckbox.tsx";
import axios, {AxiosRequestConfig} from "axios";
import JSZip from "jszip";


function LabelPrinter() {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  // States for mandatory information
  const [itemID, setItemID] = useState<string>("");
  const [isItemLoaded, setIsItemLoaded] = useState<boolean>(false);
  const [ladenPreis, setLadenPreis] = useState<string>("");
  const [itemDescription, setItemDescription] = useState<string>("");
  const [lines, setLines] = useState<string[]>(["", "", "", "", ""]);
  const [isUVP, setIsUVP] = useState<boolean>(false);
  const [UVP, setUVP] = useState<string>("");
  const [barcode, setBarcode] = useState<string>("");
  const [imageSrc, setImageSrc] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [oversizedLines, setOversizedLines] = useState<number[]>([]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (oversizedLines.length > 0) {
      const error_str = oversizedLines
        .map((lineNumber) => `Zeile ${lineNumber+1} ist zu lang!`)
        .join("\n");
      setErrorMessage(error_str);
    } else {
      setErrorMessage(""); // Clear the error message if oversizedLines is empty
    }
  }, [oversizedLines]); // Runs whenever oversizedLines is updated

  const renderItemSearch = () => {
    const searchItemInformation = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      setImageSrc(null);
      setDownloadUrl(null);
      setLadenPreis("");
      setItemDescription("");
      setUVP("");
      setLines(["", "", "", "", ""]);
      setBarcode("");

      if (itemID === "") {
        const err = "Bitte gebe eine Item ID ein";
        setErrorMessage(err);
        alert(err);
      }

      const data = {
        item_id: itemID,
        use_uvp: true
      };
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };
      await axios.post(
        `${backendUrl}/label_printer/get_item_information/`,
        data,
        config
      )
        .then((res) => {
          const data = res.data
          setIsItemLoaded(true);
          setItemDescription(data.names)
          setBarcode(data.barcode)
          setLadenPreis(data.price)
          if (data.uvp === null){
            alert("UVP nicht verfügbar!");
            setIsUVP(false);
          }
          setUVP(data.uvp);
        })
        .catch((err) => {
          let error_str = null;
          if (!err.response) {
            // This will handle network errors or cases where the server doesn't respond at all
            error_str = "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar.";
          } else if (err.response.status === 500) {
            error_str = "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan).";
          } else {
            error_str = err.response.data.detail.toString();
          }
          setErrorMessage(error_str);
          alert(error_str);
        });
    }

    return (
      <div className="LabelPrinterPage">
        <h3>Erstelle mit diesem Programm Preisschilder!</h3>
        <div className="item-search">
          <CustomTextField
            type="text"
            label="Artikel ID"
            placeholder="Artikel ID"
            is_required={true}
            errorText="Dieses Feld muss ausgefüllt sein"
            value={itemID}
            inputMode="integer"
            onChange={(e) => {setItemID(e.target.value); setIsItemLoaded(false); setImageSrc(null); setLadenPreis("");
            setUVP(""); setItemDescription(""); setLines(["", "", "", "", ""]); setBarcode(""); setDownloadUrl("");}}
            sx = {{ width: "50%" }}
          />
          <SubmitButton label="Artikel suchen" onClick={searchItemInformation} sx={{ width: "30%" }}/>
        </div>
      </div>
    );
  };

  const handleLineChange = (index: number, value: string) => {
    const updatedLines = [...lines];
    updatedLines[index] = value;
    setLines(updatedLines);
  };

  const renderItemInformation = () => {
    return (
      <div className="item-information">
        <CustomTextField
          type="text"
          label="Ladenpreis"
          placeholder="Ladenpreis"
          is_required={true}
          errorText="Dieses Feld muss ausgefüllt sein"
          endAdornment = "€"
          value={ladenPreis}
          inputMode="decimal"
          onChange={(e) => {setLadenPreis(e.target.value);}}
          sx = {{ width: "100%" }}
        />
        <CustomTextField
          type="text"
          label="Artikelinformationen"
          placeholder="Hier stehen nach der Artikelsuche die Artikelinformationen"
          is_required={false}
          value={itemDescription}
          inputMode="text"
          onChange={(e) => {setItemDescription(e.target.value);}}
          is_multiline={true}
          is_disabled={true}
          sx = {{ width: "100%" }}
        />
        <label className="instructionLabel"><b>Füge nun in jede Zeile den Text ein, der in dieser stehen soll.</b></label>
        {/* Render lines 1 to 5 dynamically */}
        {lines.map((line, index) => (
          <CustomTextField
            key={index}
            type="text"
            label={`Zeile ${index + 1}`}
            placeholder={`Zeile ${index + 1}`}
            is_required={false}
            value={line}
            inputMode="text"
            errorText={"Zeile " + (index+1) + " ist zu lang!"}
            manualError={oversizedLines.includes(index)}
            onChange={(e) => {handleLineChange(index, e.target.value); setDownloadUrl(null);}}
            sx={{ width: "100%" }}
          />
        ))}
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (errorMessage) {
      return (
        <div className="error-message-container">
          {" "}
          <p>{errorMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderGenerateAndDownloadButtons = () => {
    const handleGenerateLabel = async () => {
      const data = {
        item_id: itemID,
        lines: lines,
        barcode: barcode,
        price: ladenPreis,
        uvp: (isUVP && UVP !== null) ? UVP : null,
      };
      const config : AxiosRequestConfig  = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob", // Necessary to handle binary files (e.g., ZIP)
      };
      try {
        const response = await axios.post(`${backendUrl}/label_printer/get_label/`, data, config);

        const zipBlob = response.data;

        // Unzip the files and handle them
        const zip = await JSZip.loadAsync(zipBlob);

        // Extract the PDFs
        const pngFile = await zip.file("label_with_background.png").async("uint8array");
        const labelWithoutBackground = await zip.file("label_without_background.pdf").async("blob");

        // Create object URLs for the PDFs
        const imgBlob = new Blob([pngFile as any], {type: 'image/png'});
        const imgUrl = URL.createObjectURL(imgBlob);
        setImageSrc(imgUrl);
        const withoutBgUrl = URL.createObjectURL(new Blob([labelWithoutBackground], { type: 'application/pdf' }));

        // Set URLs for displaying and downloading
        setDownloadUrl(withoutBgUrl);
        console.log(downloadUrl)

        setOversizedLines([]);
        setErrorMessage(null); // Clear previous error
      } catch (err) {
        setImageSrc(null);
        setDownloadUrl(null);
        if (err.response) {
          const errorData = await err.response.data.text();
          const errorJson = await JSON.parse(errorData);
          setOversizedLines(errorJson.detail);
        }
        else {
          console.error(err.response);
          setErrorMessage("Error generating the label. Please try again.");
        }
      }
    };

    const handleDownloadWithoutBackground = () => {
      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      }
    };
    return (
      <div style={{display: "flex", flexDirection: "column", gap: "15px"}}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "25px",
            alignItems: "center",
          }}
        >
          <SubmitButton
            onClick={handleGenerateLabel}
            label={
              "Label erstellen"
            }
            size="small"
            isDisabled={!isItemLoaded}
          />
          <CustomCheckbox
            value={isUVP}
            label="UVP Preis anzeigen"
            onChange={checked => setIsUVP(checked)}
            is_disabled={UVP === null}
          />
        </div>
        {imageSrc && (
          <div>
            <img src={imageSrc} alt="Label with background" style={{ width: "100%", maxHeight: "500px", objectFit: "contain"}} />
          </div>
        )}
        <SubmitButton
          onClick={handleDownloadWithoutBackground}
          label={
            "Downloaden"
          }
          size="small"
          isDisabled={!isItemLoaded || !downloadUrl}
        />
      </div>
    );
  };

  return (
    <>
      <Navbar/>
      <Helmet>
        <title>{"Preisschild drucken"}</title>
      </Helmet>
      {renderItemSearch()}
      {renderItemInformation()}
      {renderGenerateAndDownloadButtons()}
      {renderErrorMessage()}
      {ColorModeIcon()}
    </>
  );
}

export default LabelPrinter;
